<template>
  <div class="reused-voucher-preview">
    <!-- Not found -->
    <div
      v-if="resolvedVoucher.notFound"
      class="preview-not-found"
    >
      <i class="fas fa-exclamation-triangle" />
      {{ t('automatedScenarios.actions.sending_channel.voucher_preview.not_found') }}
    </div>

    <!-- Expiring voucher info (from parent filter) -->
    <div
      v-else-if="resolvedVoucher.isExpiringVoucher"
      class="preview-single preview-expiring"
    >
      <div class="preview-header preview-header--expiring">
        <i class="far fa-clock" />
        {{ t('automatedScenarios.actions.sending_channel.voucher_preview.expiring_voucher_title') }}
      </div>
      <div
        v-if="resolvedVoucher.expiringVoucherInfo"
        class="preview-configs"
      >
        <div class="voucher-summary">
          <div
            v-if="resolvedVoucher.expiringVoucherInfo.timePeriod"
            class="voucher-summary-row"
          >
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.voucher_preview.expiring_period') }}</span>
            <span class="voucher-summary-value">{{ getExpiringPeriodLabel(resolvedVoucher.expiringVoucherInfo.timePeriod) }}</span>
          </div>
          <div
            v-if="resolvedVoucher.expiringVoucherInfo.codePrefix"
            class="voucher-summary-row"
          >
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.voucher_preview.expiring_code_prefix') }}</span>
            <span class="voucher-summary-value">{{ resolvedVoucher.expiringVoucherInfo.codePrefix }}</span>
          </div>
        </div>
      </div>
      <div
        v-else
        class="preview-note"
      >
        <i class="fas fa-info-circle" />
        {{ t('automatedScenarios.actions.sending_channel.voucher_preview.expiring_no_filter') }}
      </div>
    </div>

    <!-- Single path -->
    <div
      v-else-if="!resolvedVoucher.hasMultiplePaths"
      class="preview-single"
    >
      <div class="preview-header">
        <i class="far fa-link" />
        {{ t('automatedScenarios.actions.sending_channel.voucher_preview.reusing_from', { source: getStepLabel(resolvedVoucher.configs[0]) }) }}
      </div>
      <div class="preview-configs">
        <div
          v-for="(config, templateId) in getDisplayConfigs(resolvedVoucher.configs[0])"
          :key="templateId"
          class="voucher-summary"
        >
          <div class="voucher-summary-row">
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.method.label') }}</span>
            <span class="voucher-summary-value">{{ getMethodLabel(config) }}</span>
          </div>

          <div
            v-if="config.method === METHODS.MANUAL && config.identifiant"
            class="voucher-summary-row"
          >
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.manual_code.label') }}</span>
            <span class="voucher-summary-value">{{ config.identifiant }}</span>
          </div>

          <div class="voucher-summary-row">
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.type.label') }}</span>
            <span class="voucher-summary-value">{{ getTypeLabel(config) }}</span>
          </div>

          <div
            v-if="getAmountDisplay(config)"
            class="voucher-summary-row"
          >
            <span class="voucher-summary-label">
              {{ config.type === TYPES.PERCENTAGE
                ? t('automatedScenarios.actions.sending_channel.vouchers_config.amount.label_percentage')
                : t('automatedScenarios.actions.sending_channel.vouchers_config.amount.label_amount')
              }}
            </span>
            <span class="voucher-summary-value">{{ getAmountDisplay(config) }}</span>
          </div>

          <div
            v-if="config.method === METHODS.AUTOMATIC && config.type === TYPES.DUPLICATED && config.duplicate"
            class="voucher-summary-row"
          >
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.duplicate_code.label') }}</span>
            <span class="voucher-summary-value">{{ config.duplicate }}</span>
          </div>

          <div
            v-if="config.method === METHODS.AUTOMATIC && config.minorderamount"
            class="voucher-summary-row"
          >
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.minimum_amount.label') }}</span>
            <span class="voucher-summary-value">{{ config.minorderamount }}</span>
          </div>

          <div
            v-if="config.method === METHODS.AUTOMATIC && getValidityLabel(config)"
            class="voucher-summary-row"
          >
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.label') }}</span>
            <span class="voucher-summary-value">{{ getValidityLabel(config) }}</span>
          </div>

          <div
            v-if="config.method === METHODS.AUTOMATIC && config.prefix"
            class="voucher-summary-row"
          >
            <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.prefix.label') }}</span>
            <span class="voucher-summary-value">{{ config.prefix }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Multiple paths -->
    <div
      v-else
      class="preview-multiple"
    >
      <div class="preview-header preview-header--multiple">
        <i class="fas fa-code-branch" />
        {{ t('automatedScenarios.actions.sending_channel.voucher_preview.multiple_paths') }}
      </div>

      <div
        v-for="resolved in resolvedVoucher.configs"
        :key="resolved.sourceStepId"
        class="preview-path"
      >
        <div class="preview-path-label">
          {{ t('automatedScenarios.actions.sending_channel.voucher_preview.path_via', { source: getStepLabel(resolved) }) }}
        </div>
        <div class="preview-configs">
          <div
            v-for="(config, templateId) in getDisplayConfigs(resolved)"
            :key="templateId"
            class="voucher-summary"
          >
            <div class="voucher-summary-row">
              <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.method.label') }}</span>
              <span class="voucher-summary-value">{{ getMethodLabel(config) }}</span>
            </div>

            <div
              v-if="config.method === METHODS.MANUAL && config.identifiant"
              class="voucher-summary-row"
            >
              <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.manual_code.label') }}</span>
              <span class="voucher-summary-value">{{ config.identifiant }}</span>
            </div>

            <div class="voucher-summary-row">
              <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.type.label') }}</span>
              <span class="voucher-summary-value">{{ getTypeLabel(config) }}</span>
            </div>

            <div
              v-if="getAmountDisplay(config)"
              class="voucher-summary-row"
            >
              <span class="voucher-summary-label">
                {{ config.type === TYPES.PERCENTAGE
                  ? t('automatedScenarios.actions.sending_channel.vouchers_config.amount.label_percentage')
                  : t('automatedScenarios.actions.sending_channel.vouchers_config.amount.label_amount')
                }}
              </span>
              <span class="voucher-summary-value">{{ getAmountDisplay(config) }}</span>
            </div>

            <div
              v-if="config.method === METHODS.AUTOMATIC && config.type === TYPES.DUPLICATED && config.duplicate"
              class="voucher-summary-row"
            >
              <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.duplicate_code.label') }}</span>
              <span class="voucher-summary-value">{{ config.duplicate }}</span>
            </div>

            <div
              v-if="config.method === METHODS.AUTOMATIC && config.minorderamount"
              class="voucher-summary-row"
            >
              <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.minimum_amount.label') }}</span>
              <span class="voucher-summary-value">{{ config.minorderamount }}</span>
            </div>

            <div
              v-if="config.method === METHODS.AUTOMATIC && getValidityLabel(config)"
              class="voucher-summary-row"
            >
              <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.label') }}</span>
              <span class="voucher-summary-value">{{ getValidityLabel(config) }}</span>
            </div>

            <div
              v-if="config.method === METHODS.AUTOMATIC && config.prefix"
              class="voucher-summary-row"
            >
              <span class="voucher-summary-label">{{ t('automatedScenarios.actions.sending_channel.vouchers_config.prefix.label') }}</span>
              <span class="voucher-summary-value">{{ config.prefix }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="preview-note">
        <i class="fas fa-info-circle" />
        {{ t('automatedScenarios.actions.sending_channel.voucher_preview.runtime_note') }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  voucherMethodsValues,
  voucherTypeValues,
  reusePreviousCouponValues,
} from '@/composables/flows';
import type {
  ResolvedReusedVoucher,
  ResolvedParentVoucherConfig,
  MultiVoucherConfigItem,
} from '@/composables/flows';
import { getOperatorById } from '@/components/flows/operators/registry';

const VALIDITY_MAP: Record<number, string> = {
  1: 'one_day', 2: 'two_day', 3: 'three_day', 4: 'four_day',
  5: 'five_day', 6: 'six_day', 7: 'one_week', 14: 'two_week',
  21: 'three_week', 28: 'four_week', 35: 'five_week', 42: 'six_week',
  49: 'seven_week', 56: 'eight_week', 70: 'ten_week', 84: 'twelve_week',
  98: 'fourteen_week', 112: 'sixteen_week', 126: 'eighteen_week',
  140: 'twenty_week', 154: 'twenty_two_week', 168: 'twenty_four_week',
  182: 'twenty_six_week',
};

export default defineComponent({
  name: 'ReusedVoucherPreview',

  props: {
    resolvedVoucher: {
      type: Object as PropType<ResolvedReusedVoucher>,
      required: true,
      default: () => ({ configs: [], hasMultiplePaths: false, notFound: true }),
    },
  },

  setup(props) {
    const { t } = useI18n();

    const METHODS = voucherMethodsValues;
    const TYPES = voucherTypeValues;

    const UNIT_LABELS: Record<string, string> = {
      DAY: 'automatedScenarios.actions.sending_channel.voucher_preview.expiring_unit_day',
      WEEK: 'automatedScenarios.actions.sending_channel.voucher_preview.expiring_unit_week',
      MONTH: 'automatedScenarios.actions.sending_channel.voucher_preview.expiring_unit_month',
    };

    const getExpiringPeriodLabel = (timePeriod: { value: number; unit: string }): string => {
      const unitKey = UNIT_LABELS[timePeriod.unit];
      if (unitKey) {
        return `${timePeriod.value} ${t(unitKey, timePeriod.value)}`;
      }
      return `${timePeriod.value} ${timePeriod.unit.toLowerCase()}(s)`;
    };

    const getStepLabel = (resolved: ResolvedParentVoucherConfig): string => {
      if (resolved.sourceLabel) return resolved.sourceLabel;
      const operator = getOperatorById(resolved.sourceStepType);
      if (operator) {
        return t(operator.label);
      }
      return resolved.sourceStepType;
    };

    const getDisplayConfigs = (resolved: ResolvedParentVoucherConfig): Record<string, MultiVoucherConfigItem> => {
      const result: Record<string, MultiVoucherConfigItem> = {};
      for (const [templateId, config] of Object.entries(resolved.voucherConfigs)) {
        if (!config.reuse || config.reuse === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
          result[templateId] = config;
        }
      }
      return result;
    };

    const getMethodLabel = (config: MultiVoucherConfigItem): string => {
      if (config.method === METHODS.AUTOMATIC) {
        return t('automatedScenarios.actions.sending_channel.vouchers_config.method.options.automatic');
      }
      if (config.method === METHODS.MANUAL) {
        return t('automatedScenarios.actions.sending_channel.vouchers_config.method.options.manual');
      }
      return '-';
    };

    const getTypeLabel = (config: MultiVoucherConfigItem): string => {
      switch (config.type) {
        case TYPES.PERCENTAGE:
          return t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.percentage');
        case TYPES.AMOUNT:
          return t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.amount');
        case TYPES.SHIPPING_FEES:
          return t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.shipping_fees');
        case TYPES.DUPLICATED:
          return t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.duplicated');
        default:
          return '-';
      }
    };

    const getAmountDisplay = (config: MultiVoucherConfigItem): string | null => {
      if (config.type === TYPES.PERCENTAGE) {
        return `${config.amount}%`;
      }
      if (config.type === TYPES.AMOUNT) {
        return `${config.amount}`;
      }
      return null;
    };

    const getValidityLabel = (config: MultiVoucherConfigItem): string | null => {
      const days = config.validtime || 0;
      if (days === 0) return null;
      const key = VALIDITY_MAP[days];
      if (key) {
        return t(`automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.${key}`);
      }
      return `${days} day(s)`;
    };

    return {
      t,
      METHODS,
      TYPES,
      getStepLabel,
      getDisplayConfigs,
      getMethodLabel,
      getTypeLabel,
      getAmountDisplay,
      getValidityLabel,
      getExpiringPeriodLabel,
    };
  },
});
</script>

<style scoped lang="scss">
.reused-voucher-preview {
  margin-top: 1rem;

  .preview-not-found {
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-left: 4px solid #ffc107;
    border-radius: 4px;
    padding: 0.75rem 1rem;
    color: #856404;
    font-size: 0.875rem;

    i {
      margin-right: 0.5rem;
    }
  }

  .preview-header {
    font-weight: 600;
    font-size: 0.875rem;
    color: #17a2b8;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &--multiple {
      color: #6c757d;
    }

    &--expiring {
      color: #e67e22;
    }
  }

  .preview-single,
  .preview-multiple {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-left: 4px solid #17a2b8;
    border-radius: 4px;
    padding: 1rem;

    &.preview-expiring {
      border-left-color: #e67e22;
    }
  }

  .preview-path {
    margin-bottom: 0.75rem;

    &:last-of-type {
      margin-bottom: 0;
    }

    .preview-path-label {
      font-weight: 500;
      font-size: 0.8rem;
      color: #495057;
      margin-bottom: 0.5rem;
    }
  }

  .preview-note {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #dee2e6;
    font-size: 0.8rem;
    color: #6c757d;
    font-style: italic;

    i {
      margin-right: 0.25rem;
    }
  }

  .preview-configs {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .voucher-summary {
    background-color: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 0.75rem;

    .voucher-summary-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.25rem 0;
      font-size: 0.825rem;

      &:not(:last-child) {
        border-bottom: 1px solid #f1f3f5;
      }

      .voucher-summary-label {
        color: #6c757d;
        font-weight: 500;
      }

      .voucher-summary-value {
        color: #333;
        font-weight: 600;
        text-align: right;
      }
    }
  }
}
</style>
